import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { TodoList, TodoListUpdate } from './types/todoTypes';

export function useTodoListOperations(roomId: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | string | null>(null);

  const addList = useCallback(async (title: string) => {
    if (!roomId || !title.trim()) {
      console.error('Invalid parameters for addList:', { roomId, title });
      return null;
    }
    
    setLoading(true);
    setError(null);

    try {
      console.log('Starting to add list:', { title, roomId });
      
      // Default to anonymous user
      let userId = 'anonymous';
      
      // Try to get the current user if available
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          userId = user.id;
          console.log('Using authenticated user:', user.id);
        }
      } catch (authError) {
        console.log('No authenticated user, using anonymous');
      }

      const listData = {
        title: title.trim(),
        room_id: roomId,
        created_by: userId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      console.log('Inserting list data:', listData);
      
      const { data, error: supabaseError } = await supabase
        .from('todo_lists')
        .insert([listData])
        .select()
        .single();

      if (supabaseError) {
        console.error('Supabase error adding list:', supabaseError);
        throw supabaseError;
      }
      
      console.log('Successfully added list:', data);
      return data;
    } catch (err) {
      console.error('Error in addList:', err);
      setError(err instanceof Error ? err : new Error('Failed to add list'));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [roomId]);

  const updateList = useCallback(async (listId: string, updates: TodoListUpdate) => {
    if (!listId) {
      console.error('No list ID provided for update');
      return null;
    }
    
    setLoading(true);
    setError(null);

    try {
      console.log('Updating list:', { listId, updates });
      
      const updateData = {
        ...updates,
        updated_at: new Date().toISOString()
      };
      
      console.log('Sending update to database:', updateData);
      
      const { data, error: supabaseError } = await supabase
        .from('todo_lists')
        .update(updateData)
        .eq('id', listId)
        .select()
        .single();

      if (supabaseError) {
        console.error('Supabase error updating list:', supabaseError);
        throw supabaseError;
      }
      
      console.log('Successfully updated list:', data);
      return data;
    } catch (err) {
      console.error('Error in updateList:', err);
      setError(err instanceof Error ? err : new Error('Failed to update list'));
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteList = useCallback(async (listId: string): Promise<boolean> => {
    if (!listId) {
      console.error('No list ID provided for deletion');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      console.log(`Starting to delete list ${listId}...`);
      
      // First, verify the list exists and get its details for logging
      const { data: listData, error: fetchError } = await supabase
        .from('todo_lists')
        .select('id, title, room_id')
        .eq('id', listId)
        .single();
      
      if (fetchError) {
        if (fetchError.code === 'PGRST116') {
          console.log(`List ${listId} not found, nothing to delete`);
          return true; // Consider it a success if it doesn't exist
        }
        throw fetchError;
      }
      
      console.log(`Found list to delete:`, listData);
      
      // Delete the list - database will handle task deletion via CASCADE
      const { error: deleteError } = await supabase
        .from('todo_lists')
        .delete()
        .eq('id', listId);

      if (deleteError) {
        console.error('Error in delete operation:', deleteError);
        throw deleteError;
      }

      console.log(`Successfully deleted list: ${listId}`);
      return true;
    } catch (err) {
      console.error('Error in deleteList:', err);
      setError(err instanceof Error ? err : new Error('Failed to delete list'));
      throw err; // Re-throw to allow callers to handle the error
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    addList,
    updateList,
    deleteList,
  };
}
